import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { DRIVING_FAQ_CATEGORIES } from "@/content/driving-corpus/faq-defs";

export const Route = createFileRoute("/en/driving/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/driving-corpus");
    return { faqs: m.DRIVING_FAQ_EN };
  },
  component: DrivingFaqHubEn,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Thai Driving Licence & International Permit — ${faqs.length} FAQs`,
        description:
          "Thai driving licences for residents and foreigners: two-year and five-year classes, renewals and DLT e-Learning, residence certificate requirements, using a foreign licence, IDPs under the 1949 Geneva and 1968 Vienna Conventions, certified translations, insurance and penalties.",
        path: "/en/driving/faq",
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/driving/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Driving Licence FAQ", url: `${site}/en/driving/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/driving/faq")) },
      ],
    };
  },
});

function DrivingFaqHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Driving Licence FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Thai Driving Licences and International Driving Permits — {faqs.length} FAQs
        </h1>
        <p className="mb-6 text-muted-foreground">
          Answers reference the Department of Land Transport (dlt.go.th), the Motor Vehicle Act B.E. 2522, the Land
          Traffic Act B.E. 2522, the Department of Consular Affairs (consular.mfa.go.th) and the 1949 Geneva and 1968
          Vienna Conventions on Road Traffic. This is general information; rules and fees change, so confirm with the
          Land Transport office before filing.
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/driving/faq" className="text-primary underline">FAQ ใบขับขี่ไทยและใบขับขี่สากล →</a>
        </div>
        <section aria-label="Topics" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">Browse by topic</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {DRIVING_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/driving/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleEn}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Talk to our team</h2>
          <p className="text-sm text-muted-foreground">
            Ask about documents, certified licence translations and appointment booking by phone, LINE or email,
            Monday to Saturday 9:00–18:00.
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact us</a>
        </section>
        <FaqSourceNote topic="driving" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
